/* ==========================================================================
   Candie Panties — page behaviour
   No dependencies. Everything hangs off data-* hooks so markup can move into
   Shopify sections without touching this file.
   ========================================================================== */
(function () {
  "use strict";

  const mobileQuery = window.matchMedia("(max-width: 900px)");

  /* ------------------------------------------------------------------
     Mobile nav
     ------------------------------------------------------------------ */
  function initMobileNav() {
    const toggle = document.querySelector("[data-nav-toggle]");
    const nav = document.getElementById("mobile-nav");
    if (!toggle || !nav) return;

    const setOpen = (open) => {
      toggle.setAttribute("aria-expanded", String(open));
      toggle.setAttribute("aria-label", open ? "Close menu" : "Open menu");
      nav.dataset.open = String(open);
    };

    toggle.addEventListener("click", () => {
      setOpen(toggle.getAttribute("aria-expanded") !== "true");
    });

    nav.querySelectorAll("[data-nav-close]").forEach((el) => {
      el.addEventListener("click", () => setOpen(false));
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") setOpen(false);
    });

    mobileQuery.addEventListener("change", (e) => {
      if (!e.matches) setOpen(false);
    });
  }

  /* ------------------------------------------------------------------
     Carousels — scroll-snap tracks with dot pagination.
     Dots only render while the track actually overflows (mobile).
     ------------------------------------------------------------------ */
  function initCarousel(root) {
    const track = root.querySelector("[data-carousel-track]");
    const dotsWrap = root.querySelector("[data-carousel-dots]");
    if (!track || !dotsWrap) return;

    const slides = Array.from(track.children);
    let dots = [];
    let raf = 0;

    const activeIndex = () => {
      const center = track.scrollLeft + track.clientWidth / 2;
      let best = 0;
      let bestDist = Infinity;
      slides.forEach((slide, i) => {
        const mid = slide.offsetLeft + slide.offsetWidth / 2;
        const dist = Math.abs(mid - center);
        if (dist < bestDist) { bestDist = dist; best = i; }
      });
      return best;
    };

    const paint = () => {
      const i = activeIndex();
      dots.forEach((d, j) => d.setAttribute("aria-current", String(i === j)));
      root.dispatchEvent(new CustomEvent("carousel:change", { detail: { index: i } }));
    };

    const build = () => {
      dotsWrap.innerHTML = "";
      dots = slides.map((_, i) => {
        const b = document.createElement("button");
        b.type = "button";
        b.className = "carousel__dot";
        b.setAttribute("aria-label", `Go to slide ${i + 1}`);
        b.addEventListener("click", () => goTo(i));
        dotsWrap.appendChild(b);
        return b;
      });
      paint();
    };

    const goTo = (i) => {
      const slide = slides[i];
      if (!slide) return;
      const gutter = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
      const snapsToStart = getComputedStyle(slide).scrollSnapAlign.startsWith("start");
      const left = snapsToStart
        ? slide.offsetLeft - gutter
        : slide.offsetLeft - (track.clientWidth - slide.offsetWidth) / 2;
      track.scrollTo({ left, behavior: "smooth" });
    };

    track.addEventListener("scroll", () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(paint);
    }, { passive: true });

    window.addEventListener("resize", paint);
    build();

    root.carouselGoTo = goTo;
  }

  /* ------------------------------------------------------------------
     Product gallery — thumbs (desktop) swap the main image; on mobile the
     main image is the carousel itself.
     ------------------------------------------------------------------ */
  function initGallery() {
    const gallery = document.querySelector("[data-gallery]");
    if (!gallery) return;

    const track = gallery.querySelector("[data-carousel-track]");
    const slides = Array.from(gallery.querySelectorAll("[data-gallery-slide]"));
    const thumbs = Array.from(document.querySelectorAll("[data-gallery-thumb]"));
    let current = 0;

    const showDesktop = (i) => {
      slides.forEach((s, j) => { s.hidden = j !== i; });
      thumbs.forEach((t) => t.setAttribute("aria-current", String(Number(t.dataset.galleryThumb) === i)));
      current = i;
    };

    const show = (i) => {
      if (mobileQuery.matches) {
        slides.forEach((s) => { s.hidden = false; });
        gallery.carouselGoTo && gallery.carouselGoTo(i);
      } else {
        showDesktop(i);
      }
    };

    thumbs.forEach((t) => {
      t.addEventListener("click", () => show(Number(t.dataset.galleryThumb)));
    });

    // Keep desktop/mobile modes in sync when the viewport crosses the breakpoint.
    const sync = () => {
      if (mobileQuery.matches) {
        slides.forEach((s) => { s.hidden = false; });
        track.scrollLeft = 0;
      } else {
        showDesktop(current);
      }
    };
    mobileQuery.addEventListener("change", sync);
    sync();

    gallery.showImage = show;
  }

  /* ------------------------------------------------------------------
     Slide-out cart — port of the Pulchra Romance drawer, without jQuery.
     Renders from /cart.js. Once three or more Candie Panties are in the
     cart the bundle code is applied in the background (/cart/update.js
     `discount`), so the drawer total matches the advertised set price
     whether the shopper picked the Full Set tier or used "Complete the set".
     ------------------------------------------------------------------ */
  function initCartDrawer() {
    const root = document.querySelector("[data-cart-drawer]");
    if (!root || !window.fetch) return null;

    const shop = window.CandieShop || {};
    const meta = window.CandieCartMeta || { variants: {} };
    const body = root.querySelector("[data-cart-body]");
    const foot = root.querySelector("[data-cart-foot]");
    const panel = root.querySelector(".cd__panel");
    const totalEl = root.querySelector("[data-cart-total]");
    const saveRow = root.querySelector("[data-cart-savings]");
    const saveAmt = root.querySelector("[data-cart-savings-amt]");
    let lastFocus = null;
    let busy = false;
    let upsellPool = null;

    const money = (cents) => {
      const amount = (Number(cents) / 100).toFixed(2);
      const fmt = shop.moneyFormat || "${{amount}}";
      return fmt.replace(/\{\{\s*(amount|amount_with_comma_separator|amount_no_decimals|amount_with_space_separator)\s*\}\}/g, amount);
    };
    const esc = (str) => String(str == null ? "" : str).replace(/[&<>"']/g, (c) => (
      { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
    ));
    const api = (url, data) => fetch(url, data ? {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(data)
    } : { headers: { Accept: "application/json" } }).then((r) => r.json());

    // Shopify CDN images resize on the fly with a width param.
    const thumb = (url) => url ? url + (url.indexOf("?") > -1 ? "&" : "?") + "width=160" : "";
    const isCandie = (item) => item.handle === meta.productHandle;

    const setCount = (n) => {
      document.querySelectorAll("[data-cart-count]").forEach((el) => {
        el.textContent = n;
        if (el.classList.contains("header__cart-count")) el.hidden = n === 0;
      });
    };

    // Three or more Candie Panties: make sure the set price applies.
    const ensureBundle = (cart) => {
      const qty = cart.items.filter(isCandie).reduce((n, i) => n + i.quantity, 0);
      const has = (cart.discount_codes || []).some((d) => d.code === meta.bundleCode);
      if (qty < 3 || has || !meta.bundleCode) return Promise.resolve(cart);
      return api("/cart/update.js", { discount: meta.bundleCode });
    };

    const itemHtml = (item, idx) => {
      const vm = meta.variants[item.variant_id];
      const variant = vm ? vm.name : (item.variant_title || "");
      const img = vm ? vm.image : thumb(item.image);
      const props = Object.entries(item.properties || {})
        .filter(([k, v]) => v && k.charAt(0) !== "_")
        .map(([k, v]) => `<span class="cd-item__meta">${esc(k)}: ${esc(v)}</span>`).join("");
      const was = item.original_line_price > item.final_line_price
        ? `<s class="cd-item__was">${money(item.original_line_price)}</s>` : "";
      return `
        <div class="cd-item" data-line="${idx + 1}">
          <img class="cd-item__img" src="${esc(img)}" alt="" width="72" height="72" loading="lazy">
          <div class="cd-item__info">
            <p class="cd-item__name">${esc(item.product_title)}</p>
            ${variant ? `<span class="cd-item__meta">${esc(variant)}</span>` : ""}
            ${props}
            <div class="cd-qty" role="group" aria-label="Quantity">
              <button type="button" class="cd-qty__btn" data-qty="${item.quantity - 1}" data-key="${esc(item.key)}" aria-label="Remove one">&minus;</button>
              <span class="cd-qty__num">${item.quantity}</span>
              <button type="button" class="cd-qty__btn" data-qty="${item.quantity + 1}" data-key="${esc(item.key)}" aria-label="Add one">+</button>
            </div>
          </div>
          <div class="cd-item__price">${was}<span>${money(item.final_line_price)}</span></div>
        </div>`;
    };

    const upsellHtml = (cart) => {
      if (!upsellPool) return "";
      const inCart = new Set(cart.items.map((i) => i.variant_id));
      const missing = upsellPool.filter((v) => !inCart.has(v.id) && v.available !== false).slice(0, 2);
      if (!missing.length) return "";
      return `
        <div class="cd-upsell">
          <p class="cd-upsell__title">Complete the set</p>
          ${missing.map((v) => {
            const vm = meta.variants[v.id] || {};
            const img = vm.image || thumb(v.featured_image && v.featured_image.src);
            return `
              <div class="cd-upsell__row">
                <img src="${esc(img)}" alt="" width="40" height="40" loading="lazy">
                <span class="cd-upsell__meta">
                  <span class="cd-upsell__name">${esc(vm.name || v.title)}</span>
                  <span class="cd-upsell__price">${money(v.price)}</span>
                </span>
                <button type="button" class="cd-upsell__add" data-upsell="${v.id}">Add</button>
              </div>`;
          }).join("")}
        </div>`;
    };

    const render = (cart) => {
      setCount(cart.item_count);
      if (!cart.item_count) {
        foot.hidden = true;
        body.innerHTML = `
          <div class="cd-empty">
            <p class="cd-empty__line">Nothing underneath. <em>Yet.</em></p>
            <a class="cd-empty__btn" href="#product" data-cart-close>Shop Candie Panties</a>
            <a class="cd-empty__link" href="/collections/for-the-intrigued">Or browse the boutique</a>
          </div>`;
        return;
      }
      foot.hidden = false;
      body.innerHTML =
        cart.items.map(itemHtml).join("") +
        upsellHtml(cart) +
        `<details class="cd-note"${cart.note ? " open" : ""}>
          <summary>Add a gift note</summary>
          <textarea name="note" form="candie-cart-form" placeholder="Written on the card inside" data-cart-note>${esc(cart.note || "")}</textarea>
        </details>`;
      totalEl.textContent = money(cart.total_price);
      const saved = cart.total_discount || 0;
      saveRow.hidden = saved <= 0;
      if (saved > 0) saveAmt.textContent = "−" + money(saved);
    };

    const refresh = () => {
      const pool = upsellPool ? Promise.resolve() :
        api("/products/" + meta.productHandle + ".js")
          .then((p) => { upsellPool = p.variants || []; })
          .catch(() => { upsellPool = []; });
      return Promise.all([api("/cart.js"), pool])
        .then(([cart]) => ensureBundle(cart))
        .then(render);
    };

    const open = () => {
      lastFocus = document.activeElement;
      root.dataset.open = "true";
      document.documentElement.classList.add("candie-cart-open");
      panel.focus({ preventScroll: true });
      return refresh();
    };
    const close = () => {
      root.dataset.open = "false";
      document.documentElement.classList.remove("candie-cart-open");
      if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    };

    document.addEventListener("click", (e) => {
      const opener = e.target.closest("[data-cart-open]");
      if (opener) { e.preventDefault(); open(); return; }
      if (root.dataset.open !== "true") return;
      if (e.target.closest("[data-cart-close]")) {
        // the empty state's "Shop" link closes the drawer and lets the
        // #product anchor scroll normally
        if (!e.target.closest("a[href^='#']")) e.preventDefault();
        close();
      }
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && root.dataset.open === "true") close();
    });

    body.addEventListener("click", (e) => {
      const q = e.target.closest("[data-key]");
      const up = e.target.closest("[data-upsell]");
      if ((!q && !up) || busy) return;
      busy = true;
      root.classList.add("is-busy");
      const req = q
        ? api("/cart/change.js", { id: q.dataset.key, quantity: Math.max(0, Number(q.dataset.qty)) })
        : api("/cart/add.js", { items: [{ id: Number(up.dataset.upsell), quantity: 1 }] });
      req.catch(() => {}).then(refresh).finally(() => {
        busy = false;
        root.classList.remove("is-busy");
      });
    });

    // keep the note on the cart even if they leave without checking out
    body.addEventListener("change", (e) => {
      if (e.target.matches("[data-cart-note]")) api("/cart/update.js", { note: e.target.value });
    });

    // back from checkout via bfcache: the count may be stale
    window.addEventListener("pageshow", (e) => {
      if (e.persisted) api("/cart.js").then((c) => setCount(c.item_count));
    });

    return { open, close, refresh, apply: (code) => api("/cart/update.js", { discount: code }) };
  }

  /* ------------------------------------------------------------------
     Buy form — Shopify aware.
     Price + tier totals recompute from the selected variant; Add to cart
     POSTs real variant ids to /cart/add.js then opens the slide-out cart
     (or, if the drawer is unavailable, sends the shopper to /cart).
     Without JS the form still submits normally (1 x selected variant).
     ------------------------------------------------------------------ */
  function initBuyForm(drawer) {
    const form = document.querySelector("[data-buy-form]");
    if (!form) return;

    const shop = window.CandieShop || {};
    const priceEl = form.querySelector("[data-price]");
    const wasEl = form.querySelector("[data-price-was]");
    const saveEl = form.querySelector("[data-save-badge]");
    const group1 = form.querySelector('[data-swatch-group="1"]');
    const group2 = form.querySelector('[data-swatch-group="2"]');
    const swatchValue = group1 && group1.querySelector("[data-swatch-value]");
    const swatchPrefix = group1 && group1.querySelector("[data-swatch-prefix]");
    const pair2Value = group2 && group2.querySelector("[data-swatch-value]");
    const addBtn = form.querySelector("[data-add-to-cart]");
    const addLabel = form.querySelector("[data-add-label]");
    const gallery = document.querySelector("[data-gallery]");
    const colourInputs = Array.from(form.querySelectorAll('input[name="id"]'));
    const pair2Inputs = Array.from(form.querySelectorAll('input[name="candie_pair2"]'));
    const tierInputs = Array.from(form.querySelectorAll('input[name="candie_bundle"]'));

    // Shopify money formatting: prices are integers in cents.
    const money = (cents) => {
      const amount = (Number(cents) / 100).toFixed(2);
      const fmt = shop.moneyFormat || "${{amount}}";
      return fmt.replace(/\{\{\s*(amount|amount_with_comma_separator|amount_no_decimals|amount_with_space_separator)\s*\}\}/g, amount);
    };

    const selectedColour = () => colourInputs.find((i) => i.checked) || colourInputs[0];
    const selectedTier = () => tierInputs.find((i) => i.checked) || tierInputs[0];
    const selectedPair2 = () => pair2Inputs.find((i) => i.checked);
    const isAvailable = (i) => !!i && i.dataset.variantAvailable !== "false";

    // one | pair | all — what the scent pickers should let the shopper do.
    const mode = () => {
      const tier = selectedTier();
      if (tier && tier.dataset.allColours === "true") return "all";
      if (tier && Number(tier.dataset.pick) === 2 && group2) return "pair";
      return "one";
    };

    const applyMode = () => {
      const m = mode();
      form.dataset.colourMode = m;
      // The sets always ship every scent, so picker 1 is locked rather than
      // pretending a choice matters.
      if (group1) group1.disabled = m === "all";
      if (group2) group2.hidden = m !== "pair";
      if (swatchPrefix) swatchPrefix.textContent = m === "pair" ? "Pair 1" : "Color";
      // First time into The Pair: pre-pick a different scent for pair 2.
      if (m === "pair" && !selectedPair2()) {
        const first = selectedColour();
        const alt = pair2Inputs.find((i) => isAvailable(i) && (!first || i.value !== first.value)) ||
                    pair2Inputs.find(isAvailable);
        if (alt) alt.checked = true;
      }
    };

    const paint = () => {
      const colour = selectedColour();
      if (!colour) return;
      const unit = Number(colour.dataset.variantPrice || 0);
      const compare = Number(colour.dataset.variantCompare || 0);

      // Tier prices are the advertised bundle prices (data-total, in cents),
      // not quantity x unit — the store's automatic/code discounts bring
      // checkout to at or below these figures.
      const tier = selectedTier();
      const total = Number((tier && tier.dataset.total) || unit);

      if (priceEl) priceEl.textContent = money(total);

      if (compare > unit) {
        const tierQty = tier && tier.dataset.allColours === "true" ? colourInputs.length : Number((tier && tier.dataset.qty) || 1);
        const compareTotal = compare * tierQty;
        if (wasEl) { wasEl.textContent = money(compareTotal); wasEl.hidden = false; }
        if (saveEl) {
          saveEl.textContent = "SAVE " + Math.round(((compare - unit) / compare) * 100) + "%";
          saveEl.hidden = false;
        }
      } else {
        if (wasEl) wasEl.hidden = true;
        if (saveEl) saveEl.hidden = true;
      }

      const m = mode();
      const inStock = colourInputs.filter(isAvailable);
      if (swatchValue) {
        swatchValue.textContent = m === "all"
          ? "All " + inStock.length + " included"
          : colour.dataset.variantName || "";
      }
      if (pair2Value) {
        const second = selectedPair2();
        pair2Value.textContent = second ? second.dataset.variantName || "" : "";
      }
      const barEl = form.querySelector("[data-atc-bar]");
      if (barEl && barEl.syncBar) barEl.syncBar();
      if (addBtn) {
        const available = m === "all" ? inStock.length > 0
          : m === "pair" ? isAvailable(colour) && isAvailable(selectedPair2())
          : isAvailable(colour);
        addBtn.disabled = !available;
        if (addLabel) addLabel.textContent = available ? "Add to Cart" : "Sold Out";
      }
    };

    // Point the gallery's lead slide at the scent just picked.
    const showScent = (i) => {
      const src = i.dataset.gallerySrc;
      const lead = document.querySelector("[data-gallery-slide]");
      if (src && lead) {
        lead.removeAttribute("srcset");
        lead.src = src;
        lead.alt = (i.dataset.variantName || "") + " — Candie Panties";
      }
      if (gallery && gallery.showImage) gallery.showImage(0);
    };
    colourInputs.concat(pair2Inputs).forEach((i) => i.addEventListener("change", () => {
      paint();
      showScent(i);
    }));
    tierInputs.forEach((i) => i.addEventListener("change", () => {
      applyMode();
      paint();
    }));
    applyMode();
    paint();

    // Countdown — ticks down from the value in data-countdown.
    const cd = form.querySelector("[data-countdown]");
    if (cd) {
      const [h, m, sec] = cd.dataset.countdown.split(":").map(Number);
      let remaining = h * 3600 + m * 60 + sec;
      const pad = (n) => String(n).padStart(2, "0");
      setInterval(() => {
        if (remaining <= 0) return;
        remaining -= 1;
        cd.textContent = `${pad(Math.floor(remaining / 3600))}:${pad(Math.floor((remaining % 3600) / 60))}:${pad(remaining % 60)}`;
      }, 1000);
    }

    // Sticky mobile buy bar: mirror the price, and show it only once the
    // real Add to cart button has scrolled out of view.
    const bar = form.querySelector("[data-atc-bar]");
    const barPrice = form.querySelector("[data-atc-bar-price]");
    const barBtn = form.querySelector("[data-atc-bar-btn]");
    if (bar && addBtn) {
      const section = document.getElementById("product");
      let ticking = false;
      const update = () => {
        ticking = false;
        const btnRect = addBtn.getBoundingClientRect();
        const onScreen = btnRect.top < window.innerHeight && btnRect.bottom > 0;
        const started = section ? section.getBoundingClientRect().top < 0 : true;
        const show = started && !onScreen;
        bar.dataset.visible = String(show);
        document.documentElement.classList.toggle("candie-atc-open", show);
      };
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      };
      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onScroll);
      update();

      // Keep the bar's price and disabled state in step with the form.
      const syncBar = () => {
        if (barPrice && priceEl) barPrice.textContent = priceEl.textContent;
        if (barBtn && addBtn) {
          barBtn.disabled = addBtn.disabled;
          barBtn.textContent = addLabel ? addLabel.textContent : "Add to Cart";
        }
      };
      form.addEventListener("change", syncBar);
      syncBar();
      bar.syncBar = syncBar;
    }

    // Build the /cart/add.js payload for the selected tier.
    const buildItems = () => {
      const tier = selectedTier();
      const colour = selectedColour();
      if (!colour) return [];
      const gift = tier && tier.dataset.gift === "true";
      const props = gift ? { properties: { "Gift wrap": "Yes" } } : {};

      if (mode() === "all") {
        // One of every colour that is in stock.
        return colourInputs
          .filter(isAvailable)
          .map((i) => Object.assign({ id: Number(i.value), quantity: 1 }, props));
      }
      if (mode() === "pair") {
        // One line per scent; the same scent twice is one line of 2.
        const second = selectedPair2() || colour;
        if (second.value === colour.value) {
          return [Object.assign({ id: Number(colour.value), quantity: 2 }, props)];
        }
        return [colour, second].map((i) => Object.assign({ id: Number(i.value), quantity: 1 }, props));
      }
      return [Object.assign({ id: Number(colour.value), quantity: Number((tier && tier.dataset.qty) || 1) }, props)];
    };

    form.addEventListener("submit", (e) => {
      if (!window.fetch) return; // let the plain form POST handle it
      e.preventDefault();
      const items = buildItems();
      if (!items.length) return;

      if (addBtn) addBtn.disabled = true;
      if (addLabel) addLabel.textContent = "Adding…";

      fetch((shop.addUrl || "/cart/add") + ".js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items })
      })
        .then((r) => r.json().then((body) => ({ ok: r.ok, body })))
        .then(({ ok, body }) => {
          if (!ok) throw new Error((body && body.description) || "Could not add to cart");
          const cart = shop.cartUrl || "/cart";
          const tier = selectedTier();
          const code = tier && tier.dataset.discount;
          // Applying the code keeps checkout at or below the advertised
          // bundle price instead of billing qty x unit.
          if (!drawer) {
            window.location.href = code
              ? "/discount/" + encodeURIComponent(code) + "?redirect=" + encodeURIComponent(cart)
              : cart;
            return;
          }
          return (code ? drawer.apply(code) : Promise.resolve())
            .then(drawer.open)
            .then(() => {
              if (addBtn) addBtn.disabled = false;
              if (addLabel) addLabel.textContent = "Add to Cart";
            });
        })
        .catch((err) => {
          if (addBtn) addBtn.disabled = false;
          if (addLabel) addLabel.textContent = "Add to Cart";
          window.alert(err.message || "Sorry, that could not be added to your cart.");
        });
    });
  }

  /* ------------------------------------------------------------------
     The product description is merchant copy and still carries the
     "ENTER DISCOUNT CODE ..." instructions. The page applies those codes
     automatically, so the lines are removed rather than shown. Done in JS
     (not Liquid) so it survives the merchant re-editing the description.
     ------------------------------------------------------------------ */
  function stripDiscountLines() {
    const rte = document.querySelector(".acc__body.rte");
    if (!rte) return;
    rte.querySelectorAll("li, p").forEach((el) => {
      if (!/ENTER\s+DISCOUNT\s+CODE/i.test(el.textContent)) return;
      const row = el.closest("li") || el;
      row.remove();
    });
  }

  /* ------------------------------------------------------------------
     FAQ — one open at a time.
     ------------------------------------------------------------------ */
  function initFaq() {
    const list = document.querySelector("[data-faq]");
    if (!list) return;
    const items = Array.from(list.querySelectorAll("details"));
    items.forEach((d) => {
      d.addEventListener("toggle", () => {
        if (!d.open) return;
        items.forEach((o) => { if (o !== d) o.open = false; });
      });
    });
  }

  /* ------------------------------------------------------------------
     Newsletter placeholder submit.
     ------------------------------------------------------------------ */
  function initNewsletter() {
    // The footer newsletter is a real Shopify {% form 'customer' %} — it posts
    // to Shopify and reloads, so there is nothing to intercept here.
  }

  /* ------------------------------------------------------------------
     Entrance motion. Content ships visible; the reveal classes are added
     only when the observer will actually run and motion is allowed.
     ------------------------------------------------------------------ */
  function initReveal() {
    if (!("IntersectionObserver" in window)) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const groups = [
      ".section-head", ".reviews__head",
      ".why-card", ".scent-card", ".review-card", ".step", ".detail-tile",
      ".details__copy", ".unbox-card__content", ".cta__content", ".faq__item"
    ];
    const els = document.querySelectorAll(groups.join(","));
    if (!els.length) return;

    document.documentElement.classList.add("candie-motion");

    // Stagger within each parent, not globally, so late sections don't lag.
    const indexInParent = new Map();
    els.forEach((el) => {
      const p = el.parentElement;
      const i = indexInParent.get(p) || 0;
      indexInParent.set(p, i + 1);
      el.style.setProperty("--reveal-i", Math.min(i, 5));
      el.classList.add("reveal-el");
    });

    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        e.target.classList.add("is-in");
        io.unobserve(e.target);
      });
    }, { rootMargin: "0px 0px -8% 0px", threshold: 0.06 });
    els.forEach((el) => io.observe(el));
  }

  /* ------------------------------------------------------------------ */
  document.querySelectorAll("[data-carousel]").forEach(initCarousel);
  initReveal();
  initMobileNav();
  initGallery();
  initBuyForm(initCartDrawer());
  stripDiscountLines();
  initFaq();
  initNewsletter();
})();
